import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, DatePicker, InputNumber, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import tmAdminExtService from '../../services/tmAdminExtService';
import { fmtTime, toastErr } from './common';
import { StreamQuickActions, StreamRef } from './opsCommon';
import StreamOpsDrawer from './StreamOpsDrawer';

const { Text } = Typography;

const TYPES = {
    virus: ['red', '病毒'], spam_rejected: ['volcano', '垃圾拒收'], auto_pause: ['orange', '自动暂停'], pause: ['orange', '暂停'],
    resume: ['green', '恢复'], ban: ['red', '封禁'], unban: ['green', '解封'], sandbox: ['gold', '沙箱'], level: ['purple', '信用等级'],
    admin: ['blue', '其他管理操作'],
};
const SOURCE = { event: '节点事件', admin: '管理员', trust: '信用等级', state: '当前状态' };

const Risk = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ current: 1, pageSize: 50 });
    const [f, setF] = useState({ types: [] });
    const [range, setRange] = useState(null);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmAdminExtService.risk({
                types: f.types.length ? f.types.join(',') : undefined, stream_id: f.stream_id || undefined, user_id: f.user_id || undefined,
                since: range?.[0]?.format('YYYY-MM-DD HH:mm:ss'), until: range?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
                page: page.current, per_page: page.pageSize,
            });
            setRows(r?.data?.items || []);
            setTotal(r?.data?.total || 0);
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [f, range, page]);

    useEffect(() => { load(); }, [load]);

    const setFilter = (patch) => { setF((x) => ({ ...x, ...patch })); setPage((p) => ({ ...p, current: 1 })); };

    const columns = [
        { title: '时间', dataIndex: 'at', width: 160, render: fmtTime },
        {
            title: '类型', dataIndex: 'type', width: 120,
            render: (v, r) => <Tooltip title={SOURCE[r.source]}><Tag color={TYPES[v]?.[0]}>{TYPES[v]?.[1] || v}</Tag></Tooltip>,
        },
        { title: '发信流', key: 's', width: 200, render: (_, r) => (r.stream ? <StreamRef stream={r.stream} onOpen={setOpenId} /> : r.stream_id ? `#${r.stream_id}` : '-') },
        { title: '用户', key: 'u', width: 160, render: (_, r) => (r.user ? `#${r.user.id} ${r.user.username || ''}` : r.user_id ? `#${r.user_id}` : '-') },
        {
            title: '内容', key: 'm',
            render: (_, r) => (
                <Tooltip title={r.detail ? <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(r.detail, null, 2)}</pre> : null}>
                    <span>{r.summary}</span>
                    {r.admin?.id ? <Text type="secondary" style={{ fontSize: 12 }}> · 管理员 #{r.admin.id}{r.admin.username ? ` ${r.admin.username}` : ''}</Text> : null}
                    {r.message_id ? <Text type="secondary" style={{ fontSize: 12 }}> · {r.message_id}</Text> : null}
                </Tooltip>
            ),
        },
        { title: '', key: 'op', width: 150, render: (_, r) => (r.stream ? <StreamQuickActions stream={r.stream} onDone={load} /> : null) },
    ];

    return (
        <Card title="风险事件" extra={<Button icon={<ReloadOutlined />} onClick={load} />}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select mode="multiple" allowClear placeholder="全部类型" style={{ minWidth: 260 }} value={f.types}
                    options={Object.entries(TYPES).map(([k, [, l]]) => ({ value: k, label: l }))} onChange={(v) => setFilter({ types: v })} />
                <InputNumber placeholder="发信流 ID" min={1} style={{ width: 120 }} onChange={(v) => setFilter({ stream_id: v })} />
                <InputNumber placeholder="用户 ID" min={1} style={{ width: 120 }} onChange={(v) => setFilter({ user_id: v })} />
                <DatePicker.RangePicker showTime value={range} onChange={(v) => { setRange(v); setPage((p) => ({ ...p, current: 1 })); }} />
            </Space>
            <Table rowKey="key" size="small" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 1000 }}
                pagination={{ ...page, total: Math.min(total, 2000), showSizeChanger: true, onChange: (current, pageSize) => setPage({ current, pageSize }) }} />
            <StreamOpsDrawer streamId={openId} onClose={() => setOpenId(null)} onChanged={load} />
        </Card>
    );
};

export default Risk;
